import styled from "@emotion/styled/macro";
import {
  tokens,
  Typography,
  LoadingIndicator,
  Snackbar,
  Button,
  SvgNames,
  SvgIcon,
  Checkbox,
  LoadingOverlay,
} from "@sunrun/experience-ui-components";
import React, { useEffect, useState } from "react";
import {
  getEnvironment,
  getSplatUserType,
} from "@sunrun/sales-experience-shared";
import { useFlags } from "flagsmith/react";
import { CardHeader } from "../molecules/Card";
import { CheckoutFooter } from "../molecules/CheckoutFooter";
import { CallpilotAppBanner } from "../molecules/CallpilotAppBanner";
import { Card } from "components/atoms/Card";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooter } from "checkout/providers/CheckoutFooterContext";
import { HyperlinkButton } from "components/atoms/HyperlinkButton";
import { INSIDE_SALES } from "fixtures/salesDivision";
import { generateVerificationCallUrl } from "services/verificationCallService";
import { FeatureFlags } from "fixtures/features";
import { useProspect } from "checkout/hooks/useCheckoutSwr";
import { updateVerificationCallODI } from "services/updateVerificationCallODI";
import { useAppSelector } from "store";
import { ErrorModal } from "components/molecules/ErrorModal";

const INSIDE_SALES_INSTRUCTIONS_OLD = [
  {
    text: "Dial line 855-711-0661 (English) or 855-711-0667 (Spanish)",
  },

  {
    text: "Please have the first two pages of your solar agreement easily accessible.",
  },
];

const INSTRUCTIONS = [
  {
    text: "The verification call will be conducted with a virtual assistant.",
  },
  {
    text: "When you click below a new tab will open, we recommended that you download the Callpilot app.",
  },
  {
    text: "Please have the first two pages of your solar agreement easily accessible.",
  },
];

const OLD_INSTRUCTIONS = [
  {
    text: "New York state dial 833.543.0929 (English) or 855.303.0929 (Spanish) and the customer must call from their phone number",
  },
  {
    text: "Use speaker phone if homeowner and solar consultant are together in person.",
  },
  {
    text: "Please have the first two pages of your solar agreement easily accessible.",
  },
];

const Instructions = ({
  instructions,
  dataTestId,
}: {
  instructions: { text: string; onClick?: () => void }[];
  dataTestId?: string;
}) => {
  return (
    <StyledUl data-testid={dataTestId}>
      {instructions.map((instruction) => (
        <li key={instruction.text}>
          {instruction.onClick ? (
            <HyperlinkButton onClick={instruction.onClick}>
              {instruction.text}
            </HyperlinkButton>
          ) : (
            <Typography size={tokens.FONT_SIZE_1} color={tokens.BRAND_HEROBLUE}>
              {instruction.text}
            </Typography>
          )}
        </li>
      ))}
    </StyledUl>
  );
};

const VerificationCallTaskView: React.FC = () => {
  const {
    refreshTasks,
    isRefetchingTasks,
    refreshAndNavigateNext,
    currentTask,
    userProfile,
    prospectId,
    selectedProposal,
    opportunityId,
  } = useCheckoutContext();

  const selectedProposalId = selectedProposal?.id;
  const env = getEnvironment();
  const { data: prospect } = useProspect(prospectId);
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const flags = useFlags([
    FeatureFlags.CHECKOUT_VERIFICATION_CALL,
    FeatureFlags.INSIDE_SALES_VERIFICATION_CALL_ENABLED,
  ]);
  const isVerificationCallFlagEnabled =
    flags[FeatureFlags.CHECKOUT_VERIFICATION_CALL]?.enabled;
  const isInsideSalesVerificationCallFlagEnabled =
    flags[FeatureFlags.INSIDE_SALES_VERIFICATION_CALL_ENABLED]?.enabled;

  const [userType, setUserType] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [verificationCallUrl, setVerificationCallUrl] = useState<string>("");
  const [showSnackbar, setShowSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const [insideSalesReadScriptChecked, setInsideSalesReadScriptChecked] =
    useState(false);
  const [
    insideSalesVerificationUpdateLoading,
    setInsideSalesVerificationCallUpdateLoading,
  ] = useState(false);
  const [insideSalesSuccessfullyUpdated, setInsideSalesSuccessfullyUpdated] =
    useState(false);
  const [insideSalesError, setInsideSalesError] = useState<string | null>(null);
  useEffect(() => {
    if (currentTask?.isComplete) {
      setInsideSalesSuccessfullyUpdated(true);
    }
    if (
      currentTask?.isComplete ||
      !isVerificationCallFlagEnabled ||
      !prospectId ||
      !prospect
    ) {
      return;
    }

    const preferredLanguage =
      prospect?.preferredLanguage === "Spanish" ? "es" : "en";
    setIsLoading(true);
    generateVerificationCallUrl(prospectId, preferredLanguage)
      .then((response) => {
        setVerificationCallUrl(response.vcUrl);
      })
      .catch(() => {
        setVerificationCallUrl("");
        setShowSnackbar({
          open: true,
          message: "Error making verification call",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    currentTask?.isComplete,
    prospectId,
    isVerificationCallFlagEnabled,
    prospect,
  ]);

  const insideSalesIframeUrlStage = `https://sunrun--uat--c.sandbox.vf.force.com/apex/VerificationLtnPage?id=${opportunityId}&isone`;
  const insideSalesIframeUrlProd = `https://sunrun--c.vf.force.com/apex/VerificationLtnPage?id=${opportunityId}&isone`;
  const insideSalesIframeUrl =
    env === "production" ? insideSalesIframeUrlProd : insideSalesIframeUrlStage;

  const INSIDE_SALES_INSTRUCTIONS = [
    {
      text: "Read the Verification Call Script",
      onClick: () => window.open(insideSalesIframeUrl, "_blank"),
    },
    {
      text: "Have the first two pages of your solar agreement easily accessible.",
    },
    {
      text: "Please make the call on a recorded line.",
    },
  ];

  useEffect(() => {
    if (userProfile) {
      setUserType(getSplatUserType(userProfile));
    }
  }, [userProfile]);

  const isInsideSales = userType === INSIDE_SALES;
  const showNewInsideSalesContent =
    isInsideSales && isInsideSalesVerificationCallFlagEnabled;

  useCheckoutFooter(
    isRefetchingTasks || insideSalesError ? null : (
      <FooterContainer>
        {showNewInsideSalesContent && (
          <StyledTypography
            size={tokens.FONT_SIZE_0}
            color={tokens.TINTS_OFF_WHITE_20}
          >
            Please ensure you've completed the Verification Call.
          </StyledTypography>
        )}
        <CheckoutFooter
          buttonText="Continue"
          onClick={refreshAndNavigateNext}
          isLoading={isRefetchingTasks}
          testId="footer-verification-call-instructions-button"
        />
      </FooterContainer>
    ),
    [
      isRefetchingTasks,
      insideSalesError,
      showNewInsideSalesContent,
      refreshAndNavigateNext,
    ]
  );

  if (isRefetchingTasks)
    return (
      <LoadingWrapper>
        <LoadingIndicator color="black" message="Loading" />
      </LoadingWrapper>
    );

  const isVericationButtonDisabled =
    currentTask?.isComplete || isLoading || !verificationCallUrl?.length;
  const instructionsText = isVerificationCallFlagEnabled
    ? INSTRUCTIONS
    : OLD_INSTRUCTIONS;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowSnackbar({
        open: true,
        message: "Call link copied successfully",
        type: "success",
      });
    } catch {
      setShowSnackbar({
        open: true,
        message:
          "Error copying verification call link. Please check your browser permissions.",
        type: "error",
      });
    }
  };
  const markAsVerificationCallDone = async () => {
    setInsideSalesVerificationCallUpdateLoading(true);
    try {
      const response = await updateVerificationCallODI(
        authKey,
        prospectId,
        selectedProposalId ?? "",
        "yes"
      );
      if (response.success) {
        await refreshTasks();
        setInsideSalesSuccessfullyUpdated(true);
        setInsideSalesVerificationCallUpdateLoading(false);
      } else {
        setInsideSalesError(response.errors[0] ?? "Unknown error");
        console.error(
          "Received an error while updating verification call ODI",
          response.errors
        );
        setShowSnackbar({
          open: true,
          message: "Error updating verification call ODI",
          type: "error",
        });
      }
    } catch (error) {
      setInsideSalesError(error as string);
      console.error("Error updating verification call ODI", error);
      setShowSnackbar({
        open: true,
        message: "Error updating verification call ODI",
        type: "error",
      });
    }
  };

  const showNewInsideSalesSuccessCard =
    isInsideSales &&
    insideSalesSuccessfullyUpdated &&
    isInsideSalesVerificationCallFlagEnabled;

  const showNewInsideSalesLoadingOverlay =
    isInsideSales &&
    insideSalesVerificationUpdateLoading &&
    isInsideSalesVerificationCallFlagEnabled;

  if (insideSalesError) {
    return (
      <ErrorModal
        context="VerificationCallTaskView"
        error={new Error(insideSalesError)}
        action={"marking verification call as complete"}
      />
    );
  }

  return (
    <>
      {showNewInsideSalesLoadingOverlay && (
        <LoadingOverlay message="Marking verification call as complete..." />
      )}
      <InformationContainer data-testid="validation-info">
        <Typography size={tokens.FONT_SIZE_1} color={tokens.BRAND_HEROBLUE}>
          Now that you’ve signed your agreement, you’ll review the key points
          with another Sunrun representative. This is for quality assurance and
          your protection.
        </Typography>
        <Typography size={tokens.FONT_SIZE_1} color={tokens.BRAND_HEROBLUE}>
          Once you’ve completed the verification call, the status might take
          several minutes to update. Please{" "}
          <HyperlinkButton
            onClick={refreshTasks}
            data-testid="refresh-verification-call"
          >
            refresh this page
          </HyperlinkButton>
          .
        </Typography>
      </InformationContainer>
      <CardsContainer>
        {showNewInsideSalesSuccessCard && (
          <Card data-testid="success-card">
            <StyledSuccessCardHeader>
              <SvgIcon
                name={SvgNames.CheckEnclosed}
                width={64}
                height={64}
                color={tokens.SUCCESS_60}
              />
              <Typography
                size={tokens.FONT_SIZE_6}
                color={tokens.BRAND_HEROBLUE}
              >
                Verification call done successfully
              </Typography>
            </StyledSuccessCardHeader>
          </Card>
        )}
        {((!insideSalesSuccessfullyUpdated &&
          isInsideSalesVerificationCallFlagEnabled) ||
          !isInsideSalesVerificationCallFlagEnabled) && (
          <Card>
            <CardHeader>
              <Typography
                size={tokens.FONT_SIZE_3}
                color={tokens.BRAND_HEROBLUE}
              >
                Instructions
              </Typography>
            </CardHeader>
            <Instructions
              instructions={
                isInsideSales
                  ? isInsideSalesVerificationCallFlagEnabled
                    ? INSIDE_SALES_INSTRUCTIONS
                    : INSIDE_SALES_INSTRUCTIONS_OLD
                  : instructionsText
              }
              dataTestId={
                isInsideSales
                  ? isInsideSalesVerificationCallFlagEnabled
                    ? "verification-instructions-inside-sales"
                    : "verification-instructions-inside-sales-old"
                  : "verification-instructions"
              }
            />
            {isVerificationCallFlagEnabled && !isInsideSales && (
              <ButtonContainer>
                <StyledAnchor
                  data-testid="make-verification-call-button"
                  href={
                    isVericationButtonDisabled ? undefined : verificationCallUrl
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={isVericationButtonDisabled}
                >
                  Make verification call
                  {isLoading && (
                    <LoadingContainer>
                      <LoadingIndicator
                        color="white"
                        width={16}
                        height={16}
                        showLoadingMessage={false}
                      />
                    </LoadingContainer>
                  )}
                </StyledAnchor>
                <Button
                  color="tertiary"
                  data-testid="copy-call-link-button"
                  size="sm"
                  iconRight={<SvgIcon name={SvgNames.Copy} />}
                  onClick={() => {
                    copyToClipboard(verificationCallUrl);
                  }}
                  disabled={isVericationButtonDisabled}
                >
                  Copy call link
                </Button>
              </ButtonContainer>
            )}
            {showNewInsideSalesContent && (
              <StyledCheckbox
                label="I acknowledge that I read the full script to the customer on a recorded line"
                checked={insideSalesReadScriptChecked}
                onChange={() =>
                  setInsideSalesReadScriptChecked(!insideSalesReadScriptChecked)
                }
              />
            )}
            {showNewInsideSalesContent && (
              <StyledButtonContainer>
                <Button
                  color="primary"
                  data-testid="mark-as-verification-call-done-button"
                  size="sm"
                  onClick={() => markAsVerificationCallDone()}
                  disabled={!insideSalesReadScriptChecked}
                >
                  Mark as verification call done
                </Button>
              </StyledButtonContainer>
            )}
          </Card>
        )}
        {isVerificationCallFlagEnabled && !isInsideSales && (
          <CallpilotAppBanner />
        )}
      </CardsContainer>
      <Snackbar
        open={showSnackbar.open}
        onClose={() =>
          setShowSnackbar({ open: false, message: "", type: "success" })
        }
        message={showSnackbar.message}
        type={showSnackbar.type as "success" | "error"}
      />
    </>
  );
};

const StyledTypography = styled(Typography)`
  width: 100%;
`;

const StyledSuccessCardHeader = styled(CardHeader)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 16px;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 0px 0px 24px 24px;
`;

const StyledButtonContainer = styled.div`
  padding: 0px 0px 24px 24px;
`;

const StyledUl = styled.ul`
  padding-right: 24px;
  margin-bottom: 24px;

  > li {
    margin-bottom: 8px;
  }
`;

const StyledAnchor = styled.a<{ disabled: boolean }>`
  text-decoration: none;
  background-color: ${tokens.BRAND_HEROBLUE};
  display: flex;
  height: 48px;
  border-radius: 80px;
  padding: 0px 32px;

  justify-content: center;
  align-items: center;

  color: ${tokens.WHITE};
  text-align: center;
  font-family: Roobert;
  font-size: ${tokens.FONT_SIZE_1}px;
  font-style: normal;
  font-weight: 500;
  line-height: ${tokens.LINE_HEIGHTS_12};
  letter-spacing: -0.32px;

  &:hover {
    opacity: 0.8;
  }
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.3;
    cursor: not-allowed;

    &:hover {
      opacity: 0.3;
      cursor: not-allowed;
    }

  `}
`;

const InformationContainer = styled.div`
  margin-bottom: 16px;
  > p {
    padding-bottom: 16px;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  > div {
    padding: 0 0;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: 400px;
`;

const ButtonContainer = styled.div`
  padding-left: 24px;
  padding-bottom: 32px;
  gap: 24px;
  display: flex;
`;

const LoadingContainer = styled.span`
  margin-left: 8px;
`;

export {
  VerificationCallTaskView,
  INSTRUCTIONS,
  OLD_INSTRUCTIONS,
  INSIDE_SALES_INSTRUCTIONS_OLD,
};
